<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-clipboard-list"></i>
          ບັນທຶກການດຳເນີນການ (Audit Logs)
          <span class="countTotal">({{ pagination.total }})</span>
        </h4>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns is-multiline">
          <div class="column is-2">
            <div class="control">
              <label class="label">ການດຳເນີນການ</label>
              <div class="select is-fullwidth">
                <select v-model="filters.action" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option v-for="action in options.actions" :key="action" :value="action">
                    {{ formatAction(action) }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ປະເພດ Resource</label>
              <div class="select is-fullwidth">
                <select v-model="filters.resource_type" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option v-for="rt in options.resource_types" :key="rt" :value="rt">
                    {{ rt }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">Operator Type</label>
              <div class="select is-fullwidth">
                <select v-model="filters.operator_type" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option v-for="ot in options.operator_types" :key="ot" :value="ot">
                    {{ ot }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">Request ID</label>
              <input
                type="text"
                class="input"
                v-model="filters.request_id"
                placeholder="req-..."
                @keyup.enter="onFilterChange"
              >
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ຈາກວັນທີ</label>
              <input type="date" class="input" v-model="filters.from_date" @change="onFilterChange">
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ຫາວັນທີ</label>
              <input type="date" class="input" v-model="filters.to_date" @change="onFilterChange">
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-2">
            <vs-button @click="onFilterChange" :loading="isLoading" class="is-fullwidth">
              <i class="fal fa-search"></i> ຄົ້ນຫາ
            </vs-button>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div class="box">
        <div v-if="isLoading" class="has-text-centered py-5">
          <i class="fal fa-spinner fa-spin fa-2x"></i>
          <p class="mt-3">ກຳລັງໂຫລດ...</p>
        </div>

        <div v-else-if="error" class="notification is-warning">
          <p>{{ error }}</p>
          <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
          <vs-button @click="fetchLogs" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>

        <div v-else-if="logs.length === 0" class="has-text-centered py-5">
          <i class="fal fa-clipboard-list fa-3x has-text-grey-light"></i>
          <p class="mt-3 has-text-grey">ບໍ່ພົບບັນທຶກການດຳເນີນການ</p>
        </div>

        <div v-else>
          <table class="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th>ວັນທີ</th>
                <th>ການດຳເນີນການ</th>
                <th>Resource</th>
                <th>ສາຂາ</th>
                <th>Operator</th>
                <th>IP</th>
                <th class="has-text-right">ລາຍລະອຽດ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td>
                  <small>{{ formatDate(log.created_at) }}</small>
                </td>
                <td>
                  <span :class="['tag', getActionTagClass(log.action)]">
                    {{ formatAction(log.action) }}
                  </span>
                </td>
                <td>
                  <span class="tag is-light">{{ log.resource_type }}</span>
                  <br>
                  <small class="has-text-grey">ID: {{ log.resource_id }}</small>
                </td>
                <td>
                  <span v-if="log.branch_id">{{ log.branch_id }}</span>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <span class="tag is-info is-light">{{ log.operator_type }}</span>
                  <br>
                  <small class="has-text-grey">ID: {{ log.operator_id }}</small>
                </td>
                <td>
                  <small class="has-text-grey">{{ log.operator_ip }}</small>
                </td>
                <td class="has-text-right">
                  <vs-button flat size="small" @click="showLogDetail(log)">
                    <i class="fal fa-eye"></i>
                  </vs-button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="pagination-wrapper">
            <nav class="pagination" role="navigation">
              <vs-button
                @click="goToPage(pagination.page - 1)"
                :disabled="pagination.page <= 1"
                flat
              >
                <i class="fal fa-chevron-left"></i> ກ່ອນໜ້າ
              </vs-button>
              <span class="pagination-info">
                ໜ້າ {{ pagination.page }} / {{ totalPages }}
                ({{ formatNumber(pagination.total) }} ລາຍການ)
              </span>
              <vs-button
                @click="goToPage(pagination.page + 1)"
                :disabled="pagination.page >= totalPages"
                flat
              >
                ຕໍ່ໄປ <i class="fal fa-chevron-right"></i>
              </vs-button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Detail Modal -->
      <vs-dialog v-model="showDetailModal" width="600px" prevent-close>
        <template #header>
          <h4>ລາຍລະອຽດ Audit Log #{{ selectedLog?.id }}</h4>
        </template>

        <div v-if="selectedLog" class="detail-body">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">ວັນທີ</span>
              <span class="detail-value">{{ formatDate(selectedLog.created_at) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">ການດຳເນີນການ</span>
              <span class="detail-value">
                <span :class="['tag', getActionTagClass(selectedLog.action)]">
                  {{ formatAction(selectedLog.action) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Resource Type</span>
              <span class="detail-value">{{ selectedLog.resource_type }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Resource ID</span>
              <span class="detail-value">{{ selectedLog.resource_id }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Operator</span>
              <span class="detail-value">{{ selectedLog.operator_type }} (ID: {{ selectedLog.operator_id }})</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">IP Address</span>
              <span class="detail-value">{{ selectedLog.operator_ip }}</span>
            </div>
            <div class="detail-item" v-if="selectedLog.request_id">
              <span class="detail-label">Request ID</span>
              <span class="detail-value"><code>{{ selectedLog.request_id }}</code></span>
            </div>
            <div class="detail-item detail-full" v-if="selectedLog.notes">
              <span class="detail-label">ໝາຍເຫດ</span>
              <span class="detail-value">{{ selectedLog.notes }}</span>
            </div>
          </div>
          <div v-if="selectedLog.old_values || selectedLog.new_values" class="detail-changes">
            <div v-if="selectedLog.old_values" class="detail-change-col">
              <span class="detail-label">ຄ່າກ່ອນ</span>
              <pre class="json-display">{{ formatJson(selectedLog.old_values) }}</pre>
            </div>
            <div v-if="selectedLog.new_values" class="detail-change-col">
              <span class="detail-label">ຄ່າໃໝ່</span>
              <pre class="json-display">{{ formatJson(selectedLog.new_values) }}</pre>
            </div>
          </div>
        </div>

        <template #footer>
          <vs-button @click="showDetailModal = false">
            ປິດ
          </vs-button>
        </template>
      </vs-dialog>
    </div>
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatJson, formatNumber } from '@/services/walletFormatters';

const DEFAULT_OPTIONS = {
  actions: [
    'UPDATE_LIMITS', 'ROLLBACK', 'MANUAL_TOP_UP', 'CANCEL_TOP_UP',
    'DEACTIVATE', 'ACTIVATE', 'CREATE_WALLET', 'BATCH_CREATE_WALLETS',
    'BATCH_SET_ACTIVE_STATUS', 'EXPORT',
    'DEDUCT', 'REFUND', 'CONFIRM', 'CREDIT_BALANCE',
    'RESERVE', 'RELEASE', 'TRANSFER_OUT', 'TRANSFER_IN',
  ],
  resource_types: ['WALLET', 'TRANSACTION', 'TOP_UP_REQUEST', 'BANK_PROVIDER', 'RESERVATION'],
  operator_types: ['SYSTEM', 'ADMIN', 'SERVICE'],
};

export default {
  name: 'AuditLogs',
  data() {
    return {
      logs: [],
      options: { ...DEFAULT_OPTIONS },
      filters: {
        action: '',
        resource_type: '',
        operator_type: '',
        request_id: '',
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
      },
      pagination: {
        page: 1,
        page_size: 20,
        total: 0,
      },
      isLoading: false,
      error: null,
      errorRequestId: null,
      showDetailModal: false,
      selectedLog: null,
      abortController: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.page_size) || 1;
    },
  },
  mounted() {
    // Search-driven: no auto-load on open; the user clicks ຄົ້ນຫາ. Only filter options are prefetched.
    this.loadOptions();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    formatNumber,
    async loadOptions() {
      try {
        const response = await walletService.getAuditLogOptions(this.$axios);
        const data = response?.data || response;
        if (data?.actions?.length) this.options.actions = data.actions;
        if (data?.resource_types?.length) this.options.resource_types = data.resource_types;
        if (data?.operator_types?.length) this.options.operator_types = data.operator_types;
      } catch (err) {
        console.warn('Failed to load audit log options, using defaults:', err);
        // Keep DEFAULT_OPTIONS — already set in data()
      }
    },

    onFilterChange() {
      this.pagination.page = 1;
      this.fetchLogs();
    },

    async fetchLogs() {
      // Cancel any in-flight request
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.page_size,
        };
        if (this.filters.action) params.action = this.filters.action;
        if (this.filters.resource_type) params.resource_type = this.filters.resource_type;
        if (this.filters.operator_type) params.operator_type = this.filters.operator_type;
        if (this.filters.request_id?.trim()) params.request_id = this.filters.request_id.trim();
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;

        const response = await walletService.listAuditLogs(
          this.$axios, params, this.abortController.signal
        );
        this.logs = response.data || [];
        this.pagination.total = response.meta?.total || 0;
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error('Failed to fetch audit logs:', err);
        const { message, requestId } = extractErrorInfo(err);
        this.error = message || 'ບໍ່ສາມາດໂຫລດບັນທຶກການດຳເນີນການໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.pagination.page = page;
      this.fetchLogs();
    },

    showLogDetail(log) {
      this.selectedLog = log;
      this.showDetailModal = true;
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return dateStr;
    },

    formatAction(action) {
      const labels = {
        UPDATE_LIMITS: 'ປັບຂີດຈຳກັດ',
        ROLLBACK: 'ຍົກເລີກ',
        MANUAL_TOP_UP: 'ເຕີມເງິນ',
        CANCEL_TOP_UP: 'ຍົກເລີກເຕີມເງິນ',
        DEACTIVATE: 'ປິດໃຊ້ງານ',
        ACTIVATE: 'ເປີດໃຊ້ງານ',
        CREATE_WALLET: 'ສ້າງກະເປົ໋າ',
        BATCH_CREATE_WALLETS: 'ສ້າງກະເປົ໋າແບບກຸ່ມ',
        BATCH_SET_ACTIVE_STATUS: 'ເປີດ/ປິດກະເປົ໋າແບບກຸ່ມ',
        EXPORT: 'ສົ່ງອອກ',
        DEDUCT: 'ຫັກເງິນ',
        REFUND: 'ຄືນເງິນ',
        CONFIRM: 'ຢືນຢັນ',
        CREDIT_BALANCE: 'ເພີ່ມຍອດ',
        RESERVE: 'ສຳຮອງ',
        RELEASE: 'ປ່ອຍສຳຮອງ',
        TRANSFER_OUT: 'ໂອນອອກ',
        TRANSFER_IN: 'ໂອນເຂົ້າ',
      };
      return labels[action] || action;
    },

    getActionTagClass(action) {
      const classes = {
        DEDUCT: 'is-danger',
        REFUND: 'is-success',
        MANUAL_TOP_UP: 'is-success',
        CREDIT_BALANCE: 'is-success',
        ROLLBACK: 'is-warning',
        CANCEL_TOP_UP: 'is-warning',
        RESERVE: 'is-warning',
        UPDATE_LIMITS: 'is-info',
        CREATE_WALLET: 'is-info',
        BATCH_CREATE_WALLETS: 'is-info',
        BATCH_SET_ACTIVE_STATUS: 'is-primary',
        RELEASE: 'is-info',
        ACTIVATE: 'is-primary',
        DEACTIVATE: 'is-dark',
        EXPORT: 'is-light',
        CONFIRM: 'is-link',
      };
      return classes[action] || 'is-light';
    },

    formatJson,
  },
};
</script>

<style scoped>
.box-title {
  margin-bottom: 1rem;
}
.box-title h4 {
  font-size: 1.25rem;
  font-weight: 600;
}
.countTotal {
  font-weight: normal;
  color: #7a7a7a;
}
.table td {
  vertical-align: middle;
}
.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
.mt-3 {
  margin-top: 1rem;
}
.detail-body {
  padding: 20px;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.detail-item.detail-full {
  grid-column: 1 / -1;
}
.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #7a7a7a;
  text-transform: uppercase;
}
.detail-value {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}
.detail-changes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}
.detail-change-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.error-ref {
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
}
.pagination-wrapper {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}
.pagination {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.pagination-info {
  color: #7a7a7a;
}
.json-display {
  background: #f5f5f5;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  overflow-x: auto;
  max-height: 150px;
}
</style>
